/**
 * ORDER_FILLED / ORDER_WORKING toast bridge (v0.5.6 AE-036).
 *
 * Subscribes to the tickets SSE stream and pops a toast when an order
 * actually fills (or is accepted-but-working at the broker in LIVE mode).
 * Renders nothing — mounted once in AppShell so fills surface no matter
 * which tab the operator is on.
 *
 * Truthfulness notes:
 *  - ORDER_FILLED is only emitted server-side when the broker reported a
 *    real execution (SIM fills instantly; LIVE fills come from Schwab's
 *    get_order polling). The toast renders the payload numbers verbatim.
 *  - The stream backfills the last 5 tickets on connect, so we gate on
 *    created_at freshness to avoid replaying old fills on page reload.
 */
import { useRef } from 'react';
import { useStream } from '../lib/useStream';
import { useToast } from '../lib/toast';

export interface FillTicket {
  id?: string;
  action?: string;
  mode?: string;
  created_at?: string;
  payload?: {
    symbol?: string;
    side?: string;
    quantity?: number;
    fill_quantity?: number;
    fill_price?: number;
    broker_status?: string;
  };
}

/** Only toast for tickets created within this window (SSE backfill guard). */
export const FRESH_WINDOW_MS = 2 * 60 * 1000;

const TOASTED_ACTIONS = new Set(['ORDER_FILLED', 'ORDER_WORKING']);

export function isFreshFillTicket(t: FillTicket, nowMs: number): boolean {
  if (!t.action || !TOASTED_ACTIONS.has(t.action)) return false;
  if (!t.created_at) return false;
  const ts = Date.parse(t.created_at);
  if (Number.isNaN(ts)) return false;
  return nowMs - ts <= FRESH_WINDOW_MS;
}

export function fillToastMessage(t: FillTicket): { message: string; tone: 'ok' | 'info' } {
  const p = t.payload ?? {};
  const side = (p.side ?? '').toUpperCase();
  const qty = p.fill_quantity || p.quantity || '';
  const sym = p.symbol ?? '';
  const live = t.mode === 'live' ? ' (LIVE)' : '';
  if (t.action === 'ORDER_FILLED') {
    const px = typeof p.fill_price === 'number' && p.fill_price > 0
      ? ` @ $${p.fill_price.toFixed(2)}`
      : '';
    return { message: `Filled: ${side} ${qty} ${sym}${px}${live}`, tone: 'ok' };
  }
  return { message: `Order working at broker: ${side} ${qty} ${sym}${live}`, tone: 'info' };
}

export function FillToasts() {
  const toast = useToast();
  const seen = useRef<Set<string>>(new Set());

  useStream({
    url: '/api/v1/stream/tickets',
    onEvent: ({ event, data }) => {
      if (event !== 'ticket') return;
      const t = data as FillTicket;
      if (!isFreshFillTicket(t, Date.now())) return;
      if (t.id) {
        if (seen.current.has(t.id)) return;
        seen.current.add(t.id);
      }
      const { message, tone } = fillToastMessage(t);
      toast.show(message, tone);
    },
  });

  return null;
}
